// Iter 127 — Help article registry.
// Each article maps to a navRegistry destination id (or a hub id).
// Articles are intentionally short, action-oriented, and skimmable.
// 8 are fully written; the other 8 are honest "Coming soon" stubs.

export const HELP_ARTICLES = [
  // ───────────────────────── CLINICAL ─────────────────────────
  {
    id: 'consent', hub: 'clinical', title: 'Consent forms',
    lastUpdated: '2026-06-19',
    summary: 'Generate procedure-specific consent forms and post-procedure leaflets.',
    steps: [
      'Go to **Clinical → Consent forms** (or press ⌘K and type "consent").',
      'Pick the procedure template — the form pre-populates the matching risk matrix and patient information.',
      'Fill the patient name, DOB, MRN, and date. The form auto-fits to exactly 2 pages for printing or PDF export.',
      'For after-care: open the same template at `/consent/:templateId/post-procedure-care` for the standalone 1-page leaflet (PIL).',
      'Use your browser **Print → Save as PDF** to capture a clean copy. The native print stylesheet hides nav and footers automatically.',
    ],
    tips: [
      'Patient-Information Leaflets (PILs) live as a separate URL so you can hand them out without printing the consent every time.',
      'Don\'t edit clinical risk lists in the form itself — they\'re tied to the procedure template and edits would only affect one print.',
    ],
    related: ['anticoag', 'preparation'],
  },
  {
    id: 'anticoag', hub: 'clinical', title: 'Anticoagulation decision engine',
    lastUpdated: '2026-06-19',
    summary: 'Pre-procedure DOAC/warfarin/antiplatelet management decisions.',
    steps: [
      'Open **Clinical → Anticoagulation**.',
      'Select the procedure category (e.g. fine-needle, soft-tissue, vascular access).',
      'Tick each anticoagulant the patient is currently on. The engine evaluates all of them together — bridging rules included.',
      'Read the recommended action: continue / hold X days / specialist input. The bleeding risk grade is shown in red/amber/green.',
      'Print or copy the recommendation block into the clinical note.',
    ],
    tips: [
      'The engine is decision-support — it does not replace specialist consultation for high-bleeding-risk patients.',
      'Hold periods are based on creatinine clearance where relevant — make sure the patient\'s renal function is current.',
    ],
    related: ['consent', 'preparation'],
  },
  {
    id: 'preparation', hub: 'clinical', title: 'Patient prep guides',
    lastUpdated: '2026-06-19',
    summary: 'Pre-procedure instructions you can hand to patients.',
    steps: [
      'Open **Clinical → Patient Prep** and search by procedure.',
      'The card shows fasting requirements, contrast considerations, and step-by-step prep.',
      'Click **Print** for a take-home version that includes clinic phone numbers.',
    ],
    tips: ['Use ⌘K to jump straight to a procedure — try "barium" or "ct head".'],
    related: ['consent'],
  },

  // ───────────────────────── KNOWLEDGE ─────────────────────────
  {
    id: 'sops', hub: 'knowledge', title: 'SOPs library + sign-offs',
    lastUpdated: '2026-06-19',
    summary: 'Standard Operating Procedures with role-based assignment & sign-offs.',
    steps: [
      'Browse **Knowledge → SOPs**. Each SOP shows version, owner, and last review date.',
      'PMs can assign an SOP to a role or to specific staff — the assignee gets it in their dashboard until they sign off.',
      'Staff click an assignment, read the SOP, and tap **I\'ve read and understood** to record their acknowledgement.',
      'Use the **PDF intake** button to drag in a new SOP — it parses pages, extracts the title, and stages it for review.',
    ],
    tips: [
      'Pinned SOPs appear at the top — use this for the 3–5 daily safety SOPs.',
      'Bulk-assign a single SOP to "all radiographers" instead of clicking each name.',
    ],
    related: ['training', 'compliance'],
  },
  {
    id: 'training', hub: 'knowledge', title: 'Kestral K4 training modules',
    lastUpdated: '2026-06-19',
    summary: 'In-house learning library + AI assistant Q&A.',
    steps: [
      'Open **Knowledge → Kestral K4**. Modules are grouped by topic (CT, MRI, Reception, Work culture).',
      'Click a module to read or run its walkthrough.',
      'Click **Chat** to ask the AI assistant a question grounded in your own SOPs and modules.',
    ],
    tips: ['New modules are seeded on backend startup — push code, restart, and they appear.'],
    related: ['sops'],
  },
  {
    id: 'mbs', hub: 'knowledge', title: 'MBS Monitor',
    lastUpdated: '2026-06-19',
    summary: 'Track Medicare item-code changes that affect your billing.',
    steps: [
      'Open **Knowledge → MBS Monitor**. The table shows watched item numbers + the last MBS change date.',
      'Add an item: click **+ Add** and enter the item number. It syncs nightly.',
      'When a code\'s fee or rules change, an alert email goes to the PM allowlist.',
    ],
    tips: [],
    stub: false,
    related: [],
  },
  {
    id: 'referrals', hub: 'knowledge', title: 'Referral Base',
    lastUpdated: '2026-06-19',
    summary: 'GP & specialist directory with bulk import.',
    steps: [
      'Open **Knowledge → Referral Base**.',
      'Search by name, suburb, or specialty.',
      'Bulk import: click **Upload** and drop an Excel of referrers — the importer dedupes on email + provider number.',
    ],
    tips: ['Use the AHPRA verification button on each row to confirm registration is current.'],
    related: ['letterhead'],
  },
  {
    id: 'quick-ref', hub: 'knowledge', stub: true,
    title: 'Quick Reference',
    summary: 'Searchable lookup of drugs, doses, protocols, and contacts.',
  },

  // ───────────────────────── OPERATIONS ─────────────────────────
  {
    id: 'roster', hub: 'operations', title: 'Rostering — full workflow',
    lastUpdated: '2026-06-19',
    summary: 'Plan shifts, publish to staff, handle leave, swaps, timesheets, and CSV payroll export.',
    steps: [
      '**Plan the week** — Operations → Rostering. Hover any cell in the week grid and click **+** to add a draft shift. Pick staff, time, site, and an optional role label.',
      '**Publish** — when the week is built, click the green **Publish N drafts** button. Each staff member gets an email with Accept / Decline links — no login required.',
      '**Swap or give-away** — staff click their own published shift pill and choose **Swap / give away**. A colleague gets an email with Accept / Decline. On accept, ownership transfers automatically.',
      '**Leave** — Leave tab → **Request leave**. PMs review on the same tab and Approve / Decline. Approved leave appears as a banner on the affected days.',
      '**Timesheets** — Timesheets tab → **Generate for current period**. Staff edit actual hours and Submit. PMs Approve and download CSV for payroll.',
      '**Subscribe to your phone** — top-right **Send to my calendar** opens a modal with webcal:// (iPhone) and https:// (Google/Outlook) subscription URLs. Updates hourly.',
      '**Public schedule** — PMs click **Public schedule** to get a no-login URL for the staff-room notice board. Rotate anytime if leaked.',
    ],
    tips: [
      'Pending shifts get an auto-reminder email after 48h.',
      'Sites colour-code pills + power the **Filter by site** strip + per-site utilisation totals.',
      'You can manage multiple sites via the **Sites** button next to **Publish**.',
    ],
    related: ['permissions', 'people'],
  },
  {
    id: 'equipment', hub: 'operations', title: 'Equipment register',
    lastUpdated: '2026-06-19',
    summary: 'Inventory, service dates, and compliance status.',
    steps: [
      'Open **Operations → Equipment register**.',
      'Filter by status (overdue / due soon / compliant) using the pill row at the top.',
      'Click any asset to edit serial number, service intervals, location, and contact for service.',
      'Add a service log entry — the next-due date is calculated from the interval.',
    ],
    tips: ['PMs get a weekly email digest of anything overdue.'],
    related: ['compliance'],
  },
  {
    id: 'permissions', hub: 'operations', title: 'Permissions — roles + per-user overrides',
    lastUpdated: '2026-06-19',
    summary: 'Decide who can see which sections of i3 — by role or by individual user.',
    steps: [
      'Open **Operations → Permissions**. The page has two tabs: **Roles** and **Users**.',
      '**Roles** tab — the 4 system roles (Admin, Superuser, PM, Staff) are seeded and immutable. Click **+ New custom role** to create one (e.g. "Radiographer", "Reception"). Tick the hubs and pages the role can see. Ticking a hub auto-includes every page in it; you can still un-tick a specific page underneath.',
      '**Users** tab — every active staff member lists with their base role + total hubs/destinations granted. Click **Manage** on any row.',
      'In the per-user editor, tick **Custom roles assigned** to give the user any extra custom roles you created.',
      'Use **Per-user overrides → grant** to add access on top of their roles.',
      'Use **Explicitly deny** to revoke access for that one user, even if a role would have granted it. Deny always wins.',
      'Hit **Save changes** — the user\'s effective access updates immediately. Next time they refresh, their nav reflects the new permissions.',
    ],
    tips: [
      'System roles can\'t be edited — create a custom role and assign it instead.',
      'Go-Live tracker is deliberately off the PM default. Grant it to a PM individually via the Users tab.',
      'The pages a user can\'t see also disappear from ⌘K search and from each hub\'s tile grid.',
    ],
    related: ['people'],
  },
  {
    id: 'people', hub: 'operations', stub: true,
    title: 'People (staff directory + licensing)',
    summary: 'Staff list, AHPRA registrations, and license expiry alerts.',
  },
  {
    id: 'letterhead', hub: 'operations', title: 'Letterhead Generator',
    lastUpdated: '2026-06-19',
    summary: 'Branded letters to referring doctors with maps + QR.',
    steps: [
      'Open **Operations → Letterhead Generator**.',
      'Pick the recipient from your Referral Base (or type a new one).',
      'Type the body. Use ⌘B for bold and ⌘I for italic.',
      'Click **Add map** to drop in a static Google Map of the practice. **Add QR** generates a QR for the website/booking link.',
      'Print directly from the browser, or click **Download PNG / SVG** for high-resolution use.',
    ],
    tips: ['Backend PDF export works in preview but isn\'t available on production — use the browser **Save as PDF** instead.'],
    related: ['referrals'],
  },
  {
    id: 'golive', hub: 'operations', title: 'Go-Live Tracker',
    lastUpdated: '2026-06-19',
    summary: '161-task practice opening checklist with notes + priority colours.',
    steps: [
      'Open **Operations → Go-Live Tracker** (PM + allowlisted users only).',
      'Filter by status or owner using the chip row at the top.',
      'Click any task to mark it Not started / In progress / Done.',
      'Add a note — pick a priority colour: **Red** (high), **Amber** (medium), **Green** (done/low). Notes auto-expand on the task card so they\'re never hidden.',
      'Edit a task to change owner, due date, or title.',
    ],
    tips: [
      'Click a priority swatch on an existing note to change its colour without re-typing.',
      'Use ⌘+↵ inside the note composer to save quickly.',
    ],
    related: ['compliance'],
  },
  {
    id: 'management', hub: 'operations', stub: true,
    title: 'Practice settings',
    summary: 'Admin tools, charters, user management.',
  },

  // ───────────────────────── COMPLIANCE ─────────────────────────
  {
    id: 'compliance', hub: 'compliance', stub: true,
    title: 'Compliance Hub (DIAS, equipment, licensing)',
    summary: 'QIP/DIAS readiness scorecards + audit-ready exports.',
  },

  // ───────────────────────── DASHBOARD ─────────────────────────
  {
    id: 'dashboard', hub: 'dashboard', title: 'Your dashboard',
    lastUpdated: '2026-06-19',
    summary: 'Your daily landing page.',
    steps: [
      'The dashboard shows your outstanding SOP sign-offs at the top.',
      'Click any assignment card to jump straight into the SOP.',
      'Use ⌘K from anywhere to jump to another section without losing your place.',
    ],
    tips: ['The current hub is shown as a coloured pill in the header — handy on shared screens.'],
  },
];

export function getHelpArticle(id) {
  return HELP_ARTICLES.find(a => a.id === id);
}

export function helpArticlesByHub() {
  const map = {};
  for (const a of HELP_ARTICLES) {
    (map[a.hub] = map[a.hub] || []).push(a);
  }
  return map;
}
